/**
 * AllViewReadOnlyBanner — small inline notice shown inside cards /
 * tabs that are restricted on the Plus tier All Portfolios view.
 *
 * Round 92o++++++++N++++++++++ (Jun 2026).
 *
 * Round P108ab (Feb 2026): the banner now doubles as an inline
 * portfolio picker. Instead of just telling users to "switch"
 * (which required them to close the modal, open the selector,
 * pick a portfolio, then re-open the modal), it now shows a
 * dropdown listing every portfolio they own. Picking one flips
 * `active_portfolio_id` on the store, which cascades to the
 * `useIsReadOnlyAllView` hook and unlocks the Add Position form
 * without a page reload. Also offers "+ Create new portfolio"
 * which pops the CreatePortfolioModal.
 *
 * Two flavors:
 *   <AllViewReadOnlyBanner verb="buy" />            → "buy / sell stocks"
 *   <AllViewReadOnlyBanner verb="DRIP" />           → "set DRIP options"
 *   <AllViewReadOnlyBanner verb="cash transactions" /> → ...
 */
import { useState } from 'react';
import { Info, ChevronDown } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { getSnapshot, subscribe, setActive } from '@/lib/portfolioStore';
import { getAccountType } from '@/lib/accountTypes';
import { getBroker } from '@/lib/brokers';
import CreatePortfolioModal from './CreatePortfolioModal';

export default function AllViewReadOnlyBanner({ verb = 'this action', compact = false }) {
  const store = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const portfolios = Array.isArray(store.portfolios) ? store.portfolios : [];
  const hasGroups = portfolios.length > 0;
  const [createOpen, setCreateOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const cls = compact
    ? 'text-[11px] px-2 py-1 rounded-md border border-violet-300 dark:border-violet-700/50 bg-violet-50/60 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200'
    : 'text-xs px-3 py-2.5 rounded-md border border-violet-300 dark:border-violet-700/50 bg-violet-50/60 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200';

  const handleSelect = async (e) => {
    const pid = e.target.value;
    if (!pid) return;
    setSwitching(true);
    try {
      await setActive(pid);
      // Store update cascades → useIsReadOnlyAllView flips → parent
      // component re-renders and the Add Position form appears in
      // place of this banner. No manual close needed.
    } catch {
      // setActive shows its own toast on failure; nothing to do here.
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className={cls} data-testid="all-view-readonly-banner">
      <div className="flex items-start gap-2">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold leading-snug mb-1.5">
            {hasGroups
              ? `All Portfolios is a summary view. Pick which portfolio to ${verb} into:`
              : `Name a portfolio first — you don't have any yet.`}
          </p>
          {hasGroups ? (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[180px]">
                <select
                  value=""
                  onChange={handleSelect}
                  disabled={switching}
                  className="w-full h-8 pl-2 pr-7 rounded-md border border-violet-300 dark:border-violet-700/60 bg-white dark:bg-zinc-900 text-[12px] text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-violet-500/40 disabled:opacity-60 disabled:cursor-wait"
                  data-testid="all-view-portfolio-picker"
                >
                  <option value="" disabled>
                    {switching ? 'Switching…' : '— Pick a portfolio —'}
                  </option>
                  {portfolios.map((p) => {
                    const acct = getAccountType(p.account_type);
                    const br = getBroker(p.broker);
                    return (
                      <option key={p.portfolio_id} value={p.portfolio_id}>
                        {p.name}
                        {acct?.label ? ` · ${acct.label}` : ''}
                        {br?.label ? ` · ${br.label}` : ''}
                      </option>
                    );
                  })}
                </select>
                <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none opacity-60" />
              </div>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="h-8 px-2.5 rounded-md border border-violet-300 dark:border-violet-700/60 bg-white/60 dark:bg-zinc-900/60 hover:bg-violet-100 dark:hover:bg-violet-950/50 text-[11px] font-semibold whitespace-nowrap transition-colors"
                data-testid="all-view-create-portfolio"
              >
                + New portfolio
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="h-8 px-3 rounded-md border border-violet-300 dark:border-violet-700/60 bg-white/60 dark:bg-zinc-900/60 hover:bg-violet-100 dark:hover:bg-violet-950/50 text-[11px] font-semibold transition-colors"
              data-testid="all-view-create-first-portfolio"
            >
              + Create your first portfolio
            </button>
          )}
        </div>
      </div>

      <CreatePortfolioModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
}
